import {
  Flex,
  Table,
  Progress,
  Icon,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Switch,
  IconButton,
  Button,
  Skeleton,
  styled,
  Tooltip,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";

// Custom components
import Card from "components/card/Card";
import Menu from "components/menu/MainMenu";

// Assets
import { MdCheckCircle, MdCancel, MdOutlineError } from "react-icons/md";
import { EditIcon } from "@chakra-ui/icons";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { UpdateCampaignStatusAction } from "views/admin/default/action";

export default function ColumnsTable(props: {
  columns: any;
  tableData: any;
  isLoading: boolean;
  showExtra: boolean;
}) {
  const dispatch = useDispatch();
  const history = useHistory();

  const { columns, tableData, isLoading } = props;

  const tableInstance = useTable(
    {
      columns: columns,
      data: tableData,
      initialState: { pageSize: 200 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  initialState.pageSize = 200;

  useEffect(() => {
    tableInstance.setPageSize(200);
  }, [tableData, columns, isLoading]);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      mt={10}
      overflowX={{ sm: "scroll", lg: "scroll" }}
    >
      <Flex px="25px" justify="space-between" mb="10px" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Events
        </Text>
      </Flex>
      <Table variant="striped" color="gray.800" mb="12px">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  pe="10px"
                  key={index}
                  borderColor={borderColor}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize={{ sm: "14px", lg: "12px" }}
                    color="gray.400"
                  >
                    {column.render("Header")}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row, index) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={index}>
                {row.cells.map((cell, index) => {
                  let data;
                  if (cell.column.Header === "NAME") {
                    data = (
                      <Text
                        _hover={{ textDecoration: "underline" }}
                        color={textColor}
                        fontSize="sm"
                        fontWeight="700"
                      >
                        {cell.value}
                      </Text>
                    );
                  } else if (cell.column.Header === "ACTIVE") {
                    data =
                      //@ts-ignore
                      cell.row.original.isDirect ? (
                        <Switch
                          onChange={() => {
                            dispatch(
                              UpdateCampaignStatusAction(
                                //@ts-ignore
                                cell.row.original.id,
                                !cell.value
                              )
                            );
                          }}
                          style={{ marginRight: -80 }}
                          isChecked={cell.value}
                          size="sm"
                        />
                      ) : null;
                  } else if (cell.column.Header === "APP") {
                    data = (
                      <img
                        referrerPolicy="no-referrer"
                        style={{
                          width: 40,
                          height: 40,
                          minWidth: 40,
                          borderRadius: 4,
                        }}
                        src={cell.value}
                      />
                    );
                  } else if (cell.column.Header === "PROGRESS") {
                    data = (
                      <Flex align="center">
                        <Progress
                          variant="table"
                          colorScheme="brandScheme"
                          h="8px"
                          w="108px"
                          value={cell.value}
                        />
                      </Flex>
                    );
                  } else if (
                    cell.column.Header.toString().includes("D") ||
                    cell.column.Header.toString() == "To Date"
                  ) {
                    const percentage =
                      cell.row.values.installs > 0
                        ? Math.round(
                            (cell.value / cell.row.values.installs) * 100
                          )
                        : 0;

                    let diff = cell.value;

                    if (cell.column.Header === "D60") {
                      diff = cell.value - cell.row.values.eventsD30;
                    }
                    if (cell.column.Header === "D30") {
                      diff = cell.value - cell.row.values.eventsD14;
                    }
                    if (cell.column.Header === "D14") {
                      diff = cell.value - cell.row.values.eventsD7;
                    }
                    if (cell.column.Header === "D7") {
                      diff = cell.value - cell.row.values.eventsD3;
                    }
                    if (cell.column.Header === "D3") {
                      diff = cell.value - cell.row.values.eventsD1;
                    }
                    if (cell.column.Header === "D1") {
                      diff = cell.value - cell.row.values.eventsD0;
                    }

                    const percentageToD0 =
                      cell.value / cell.row.values.eventsD180;

                    //@ts-ignore
                    const spend = row.original.spend;
                    const rev = getIt(row, cell.column.Header.toString());
                    const installs = cell.row.values.installs;
                    const arpu =
                      rev && installs > 0 ? Number(rev) / installs : null;

                    data = (
                      <div
                        style={{
                          width: 80,
                          borderRadius: 4,
                          backgroundColor: `rgb(40, 184, 255, ${percentageToD0})`,
                          padding: 4,
                          marginRight: -4,
                          marginLeft: -10,
                        }}
                      >
                        <Text
                          textShadow={"0.1px 0.1px black"}
                          marginBottom={0}
                          color={percentageToD0 > 0.3 ? "white" : textColor}
                          textAlign={"center"}
                          fontSize="l"
                          fontWeight="600"
                        >
                          {cell.value >= 1 && percentage == 0
                            ? "<1"
                            : percentage}
                          %
                        </Text>
                        <Text
                          color={percentageToD0 > 0.3 ? "#efefef" : "#7e7e7e"}
                          textAlign={"center"}
                          fontSize="m"
                          fontWeight="400"
                        >
                          {cell.value}
                        </Text>
                        {rev && (
                          <Text
                            color={percentageToD0 > 0.3 ? "#efefef" : "#7e7e7e"}
                            textAlign={"center"}
                            marginTop={1}
                            fontSize={15}
                            fontWeight="700"
                          >
                            {rev ? `${((rev / spend) * 100).toFixed(2)}%` : ""}
                          </Text>
                        )}
                        {arpu !== null && (
                          <Tooltip label="ARPU (revenue / installs)">
                            <Text
                              color={
                                percentageToD0 > 0.3 ? "#d7f0ff" : "#28b8ff"
                              }
                              textAlign={"center"}
                              fontSize={13}
                              fontWeight="600"
                            >
                              ${arpu.toFixed(3)}
                            </Text>
                          </Tooltip>
                        )}
                      </div>
                    );
                  } else if (cell.column.Header === "ID") {
                    data = (
                      <IconButton
                        aria-label={"edit"}
                        onClick={() => {
                          history.push("/ua/cpi/create?id=" + cell.value);
                        }}
                        icon={<EditIcon />}
                      />
                    );
                  } else if (cell.column.Header === "COUNTRIES") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {cell.value ? cell.value.join(", ") : "Global"}
                      </Text>
                    );
                  } else if (cell.column.Header === "EVENT") {
                    data = (
                      <Text color={textColor} fontSize="l" fontWeight="700">
                        {cell.value}
                      </Text>
                    );
                  } else if (cell.column.Header === "DAILY CAP") {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        ${Number(cell.value).toFixed(0)}
                      </Text>
                    );
                  } else if (
                    cell.column.Header === "SPEND" ||
                    cell.column.Header === "CPI" ||
                    cell.column.Header === "CPA"
                  ) {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        ${Number(cell.value).toFixed(2)}
                      </Text>
                    );
                  } else {
                    data = (
                      <Text color={textColor} fontSize="sm" fontWeight="500">
                        {cell.value}
                      </Text>
                    );
                  }
                  return (
                    <Td
                      {...cell.getCellProps()}
                      key={index}
                      fontSize={{ sm: "14px" }}
                      maxH="30px !important"
                      py="8px"
                      minW={{ sm: "150px", md: "200px", lg: "2px" }}
                      borderColor="transparent"
                    >
                      <Skeleton
                        borderRadius={16}
                        endColor={"rgba(53,63,255,0.17)"}
                        isLoaded={!isLoading}
                      >
                        {data}
                      </Skeleton>
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Card>
  );
}

const getIt = (row: any, header: string): any => {
  if (header == "To Date") {
    header = "D180";
  }

  return row.original["revenue" + header]
    ? row.original["revenue" + header].toFixed(2)
    : null;
};
